import { Org } from "tf-api-client";
import type { ChipColor } from "../components/Base/Chip.vue";

// Single home for licensing/suspension labels. Enums and filter integers come
// from tf-api-client; this module only maps them to i18n keys, each with a
// neutral fallback so a value the backend adds later never renders as a raw
// key or as nothing.

const STATUS_CHANGE_REASON_KEYS: Record<
  Org.OrganizationStatusChangeReason,
  string
> = {
  [Org.OrganizationStatusChangeReason.LicenseAllocationRemoved]:
    "licensing.statusChange.licenseAllocationRemoved",
  [Org.OrganizationStatusChangeReason.LicenseAllocationRestored]:
    "licensing.statusChange.licenseAllocationRestored",
  [Org.OrganizationStatusChangeReason.SubscriptionRenewalFailed]:
    "licensing.statusChange.subscriptionRenewalFailed",
  [Org.OrganizationStatusChangeReason.SubscriptionRenewed]:
    "licensing.statusChange.subscriptionRenewed",
  [Org.OrganizationStatusChangeReason.ParentOrganizationFrozen]:
    "licensing.statusChange.parentOrganizationFrozen",
  [Org.OrganizationStatusChangeReason.ParentOrganizationReactivated]:
    "licensing.statusChange.parentOrganizationReactivated",
  [Org.OrganizationStatusChangeReason.SubscriptionPaymentFailed]:
    "licensing.statusChange.subscriptionPaymentFailed",
  [Org.OrganizationStatusChangeReason.SubscriptionPaymentReceived]:
    "licensing.statusChange.subscriptionPaymentReceived",
  [Org.OrganizationStatusChangeReason.BillingSubscriptionEnded]:
    "licensing.statusChange.billingSubscriptionEnded",
};

export const STATUS_CHANGE_UNKNOWN_KEY = "licensing.statusChange.unknown";

export const statusChangeReasonKey = (reason: string): string =>
  STATUS_CHANGE_REASON_KEYS[reason as Org.OrganizationStatusChangeReason] ??
  STATUS_CHANGE_UNKNOWN_KEY;

const SUBSCRIPTION_STATUS_KEYS: Record<Org.SubscriptionStatus, string> = {
  [Org.SubscriptionStatus.Pending]: "licensing.subscriptionStatus.pending",
  [Org.SubscriptionStatus.Active]: "licensing.subscriptionStatus.active",
  [Org.SubscriptionStatus.Canceled]: "licensing.subscriptionStatus.cancelled",
  [Org.SubscriptionStatus.Suspended]: "licensing.subscriptionStatus.suspended",
  [Org.SubscriptionStatus.Terminated]:
    "licensing.subscriptionStatus.terminated",
};

export const subscriptionStatusKey = (status: string): string =>
  SUBSCRIPTION_STATUS_KEYS[status as Org.SubscriptionStatus] ??
  "licensing.subscriptionStatus.unknown";

// Suspended page: how often an unchanged suspension is re-checked.
export const SUSPENDED_POLL_INTERVAL_MS = 60_000;
export const SUSPENDED_POLL_BACKOFF_INTERVAL_MS = 5 * 60_000;
export const SUSPENDED_POLL_BACKOFF_AFTER = 10;

const SUBSCRIPTION_STATUS_COLORS: Record<Org.SubscriptionStatus, ChipColor> = {
  [Org.SubscriptionStatus.Pending]: "secondary",
  [Org.SubscriptionStatus.Active]: "success",
  [Org.SubscriptionStatus.Canceled]: "warning",
  [Org.SubscriptionStatus.Suspended]: "danger",
  [Org.SubscriptionStatus.Terminated]: "danger",
};

export const subscriptionStatusColor = (status: string): ChipColor =>
  SUBSCRIPTION_STATUS_COLORS[status as Org.SubscriptionStatus] ?? "secondary";

const SEAT_CHANGE_REASON_KEYS: Record<Org.SeatChangeReason, string> = {
  [Org.SeatChangeReason.Manual]: "licensing.seatChange.manual",
  [Org.SeatChangeReason.SelfService]: "licensing.seatChange.selfService",
  [Org.SeatChangeReason.RenewalTrueUp]: "licensing.seatChange.renewalTrueUp",
  [Org.SeatChangeReason.DowngradeApplied]:
    "licensing.seatChange.downgradeApplied",
  [Org.SeatChangeReason.BillingReconciled]:
    "licensing.seatChange.billingReconciled",
  [Org.SeatChangeReason.TrialConversion]:
    "licensing.seatChange.trialConversion",
};

export const seatChangeReasonKey = (reason: string): string =>
  SEAT_CHANGE_REASON_KEYS[reason as Org.SeatChangeReason] ??
  "licensing.seatChange.unknown";

const DOWNGRADE_STATUS_KEYS: Record<Org.ScheduledSeatDowngradeStatus, string> =
  {
    [Org.ScheduledSeatDowngradeStatus.Pending]:
      "licensing.downgradeStatus.pending",
    [Org.ScheduledSeatDowngradeStatus.Applied]:
      "licensing.downgradeStatus.applied",
    [Org.ScheduledSeatDowngradeStatus.Cancelled]:
      "licensing.downgradeStatus.cancelled",
  };

export const downgradeStatusKey = (status: string): string =>
  DOWNGRADE_STATUS_KEYS[status as Org.ScheduledSeatDowngradeStatus] ??
  "licensing.downgradeStatus.unknown";

const DOWNGRADE_STATUS_COLORS: Record<
  Org.ScheduledSeatDowngradeStatus,
  ChipColor
> = {
  [Org.ScheduledSeatDowngradeStatus.Pending]: "info",
  [Org.ScheduledSeatDowngradeStatus.Applied]: "success",
  [Org.ScheduledSeatDowngradeStatus.Cancelled]: "secondary",
};

export const downgradeStatusColor = (status: string): ChipColor =>
  DOWNGRADE_STATUS_COLORS[status as Org.ScheduledSeatDowngradeStatus] ??
  "secondary";

export const billingIntervalKey = (interval: string): string =>
  interval === Org.SubscriptionBillingInterval.Monthly ||
  interval === Org.SubscriptionBillingInterval.Annual
    ? `licensing.billingInterval.${interval}`
    : "licensing.billingInterval.unknown";

// "Renewal is being processed" is only believable for a short while after the
// term end (the daily job runs at 03:00 UTC); beyond this the job probably
// failed and the customer is told to get in touch instead.
export const RENEWAL_PROCESSING_MAX_OVERDUE_HOURS = 26;

// After returning from the billing portal: payments and card changes reach
// the backend by webhook, typically within seconds, so the page fetches once
// more after this delay.
export const BILLING_RETURN_REFETCH_DELAY_MS = 8_000;

// Self-service (`managedPayments`) customers manage or cancel their
// subscription there: Stripe, through Link, is the merchant of record.
export const LINK_URL = "https://link.com";

// The subscription page: also where a paid Checkout comes back to.
export const SUBSCRIPTION_ROUTE = "/organization/subscription";

// Seat usage at or above this percentage shows the "add seats" hint.
export const SEAT_USAGE_WARNING_PERCENT = 90;

// A trial whose end has passed is converted (or lapses) by the daily run:
// its banner re-fetches the subscription at this interval until it changes.
export const TRIAL_ENDED_REFETCH_INTERVAL_MS = 3 * 60_000;

// Trial request: "Send again" becomes available this long after a submission.
export const TRIAL_REQUEST_RESEND_DELAY_MS = 60_000;

// Whether the subscription is a trial of the organization's own (Manager or
// Standard). A Managed org sees its Manager's trial, which is not its own:
// no trial banner, upgrade or cancel there (see `getTrialStatus`).
export const isOwnTrial = (
  subscription: Org.Subscription | null | undefined,
  organizationKind: Org.OrganizationKind | null | undefined,
): boolean =>
  !!subscription &&
  !!organizationKind &&
  subscription.kind === Org.SubscriptionKind.Trial &&
  organizationKind !== Org.OrganizationKind.Managed;

// Whether the environment bills online (a payment provider): only then can
// the org admin upgrade, check out, open the billing portal or see a seat
// price. With manual billing those endpoints answer 404 and sales converts
// trials and invoices the seats ("contact your account manager"). A response
// without the flag (a backend from before it) means manual billing.
export const isSelfServiceBilling = (
  subscription: Org.Subscription | null | undefined,
): boolean => subscription?.selfServiceBilling === true;

// Billed by Stripe: seats are bought online and reductions are scheduled with
// Stripe too (from `termEndDate` it bills the reduced seats).
export const isStripeBilled = (
  subscription: Org.Subscription | null | undefined,
): boolean =>
  isSelfServiceBilling(subscription) && subscription?.billingLinked === true;

// Whether the admin can buy seats (`org.increaseSeats`): a paid, active
// subscription billed by Stripe whose term is running, in a Manager or
// Standard organization (a Managed one's seats come from its Manager). A
// trial adds seats by upgrading; with manual billing the account manager does.
export const canBuySeats = (
  subscription: Org.Subscription | null | undefined,
  organizationKind: Org.OrganizationKind | null | undefined,
  now: Date = new Date(),
): boolean =>
  !!subscription &&
  !!organizationKind &&
  organizationKind !== Org.OrganizationKind.Managed &&
  isStripeBilled(subscription) &&
  subscription.kind === Org.SubscriptionKind.Standard &&
  subscription.status === Org.SubscriptionStatus.Active &&
  new Date(subscription.termEndDate).getTime() > now.getTime();

// A seat increase waiting for its payment is paid on Stripe's page in another
// tab: the subscription page checks back at this interval.
export const SEAT_INCREASE_POLL_INTERVAL_MS = 10_000;
// The seat increase preview waits for the admin to stop typing.
export const SEAT_INCREASE_PREVIEW_DEBOUNCE_MS = 400;

// Self-service contracts: the admin picks a term and a payment option at the
// card step; the contract renews for the same term and payment option.
export const CONTRACT_TERMS: Org.ContractTermMonths[] = [12, 36];
export const PAYMENT_OPTIONS: Org.SubscriptionBillingInterval[] = [
  Org.SubscriptionBillingInterval.Monthly,
  Org.SubscriptionBillingInterval.Annual,
];

// "12-month contract, paid monthly"; empty for a payment option the dashboard
// doesn't know
export const contractDescription = (
  t: (key: string, values: Record<string, unknown>) => string,
  contractTermMonths: number,
  billingInterval: string,
): string =>
  PAYMENT_OPTIONS.includes(billingInterval as Org.SubscriptionBillingInterval)
    ? t(`licensing.contract.describe.${billingInterval}`, {
        months: contractTermMonths,
      })
    : "";
